<!-- #region HTML -->
<template>
  <div class="app">
    <!-- #region Alert Component -->
    <alert :SystemErrorAlert="isSystemError" :codeSent="codeSent" @update:SystemErrorAlert="isSystemError = $event"
      @update:codeSent="codeSent = $event">
    </alert>
    <!-- #endregion -->

    <!-- #region Main Container -->
    <div class="main-container form-container">
      <!-- #region Steps -->
      <div class="steps">
        <!-- #region Back Button -->
        <div class="back-bg" @click="goBack">
          <img class="back" src="@/assets/img/back.svg" alt="戻る" width="100%" />
        </div>
        <!-- #endregion -->

        <!-- #region Right Box -->
        <div class="right-box">
          <!-- #region Sub Container -->
          <div class="sub-container">
            <!-- #region Title -->
            <div class="title">
              <label>新規登録</label>
            </div>
            <!-- #endregion -->

            <!-- #region Step Box -->
            <div class="step-box text-left">
              <label class="step-title">ステップ 2/2</label>
              <label class="step-description sp-hide">メールに記載された認証コードを入力し、新規登録を完了してください。</label>
            </div>
            <!-- #endregion -->
          </div>
          <!-- #endregion -->

          <!-- #region Step Description Box -->
          <div class="step-description-box text-left">
            <div class="step-description">メールに記載された認証コードを入力し、新規登録を完了してください。</div>
          </div>
          <!-- #endregion -->
        </div>
        <!-- #endregion -->
      </div>
      <!-- #endregion -->

      <!-- #region Form -->
      <form novalidate @submit.prevent="validateUser">
        <!-- #region Card -->
        <md-card class="form-container">
          <!-- #region Horizontal Rule -->
          <hr>
          <!-- #endregion -->

          <!-- #region Form Fields -->
          <div class="md-form">
            <!-- #region Email ID Field -->
            <md-field>
              <div class="email-id">
                <div class="text-left display-fixed-element">
                  <span class="fixed-variables-title">メールアドレスID：</span>
                  <span class="fixed-variables-element">{{ form.mailaddress }}</span>
                  <input type="hidden" v-model="form.mailaddress" name="mailaddress" />
                </div>
              </div>
            </md-field>
            <!-- #endregion -->

            <!-- #region Verification Code Field -->
            <md-field :class="getValidationClass('verificationCode')" class="md-field-container">
              <md-input class="mb-20" id="verification-code" v-model="form.verificationCode" name="verification-code"
                placeholder="認証コード" :disabled="sending" />
              <span v-if="isInvalidCode === true" class="md-error error">認証コードが誤っています。</span>
              <span v-else-if="!$v.form.verificationCode.required" class="md-error error">入力してください。</span>
              <span v-else-if="!$v.form.verificationCode.minLength" class="md-error error">認証コードが誤っています。</span>
              <span v-else-if="!$v.form.verificationCode.maxLength" class="md-error error">認証コードが誤っています。</span>
            </md-field>
            <!-- #endregion -->

            <!-- #region Resend Code Link -->
            <div class="resend-code verify-code">
              <a :class="{ disabled: isResendCode }" @click="resendCode">認証コード再送信</a>
            </div>
            <!-- #endregion -->
          </div>
          <!-- #endregion -->

          <!-- #region Progress Bar -->
          <md-progress-bar v-if="sending" md-mode="indeterminate" />
          <!-- #endregion -->

          <!-- #region Submit Container -->
          <div class="submit-container">
            <md-card-actions>
              <md-button type="submit" class="primary-button" :disabled="sending">認証してアカウントを登録</md-button>
            </md-card-actions>
          </div>
          <!-- #endregion -->
        </md-card>
        <!-- #endregion -->

      </form>
      <!-- #endregion -->
    </div>
    <!-- #endregion -->
  </div>
</template>
<!-- #endregion -->

<!-- #region JavaScript -->

<script>
// @ is an alias to /src
import awsconfig from '@/aws-exports'
import apiUrl from '@/const/api-url.js'
import Alert from '@/layout/Alerts.vue'
import { Auth } from 'aws-amplify'
import { validationMixin } from 'vuelidate'
import { maxLength, minLength, required } from 'vuelidate/lib/validators'
import { issueShopAuthCookies } from '@/services/CookieService'

export default {
  name: 'VerifySignup',
  mixins: [validationMixin],
  components: {
    "alert": Alert
  },
  props: {},
  data: () => ({
    form: {
      mailaddress: null,
      verificationCode: null,
    },
    codeSent: false,
    userSaved: false,
    sending: false,
    message: '',
    isResendCode: false,
    isUserFound: true,
    isInvalidCode: false,
    isSystemError: false,
  }),
  validations: {
    form: {
      verificationCode: {
        required,
        minLength: minLength(6),
        maxLength: maxLength(6),
      },
    },
  },
  created() {
    this.form.mailaddress = this.$store.state.signupEmail
  },
  methods: {
    goBack() {
      window.history.back();
    },
    getValidationClass(fieldName) {
      const field = this.$v.form[fieldName]
      if (field) {
        if (fieldName == 'verificationCode') {
          if (field.$anyError) {
            this.isInvalidCode = false
          }
        }
        return {
          'md-invalid': field.$invalid && field.$dirty || this.isInvalidCode,
        }
      }
    },
    clearForm() {
      this.$v.$reset()
      this.form.verificationCode = null
    },
    clearErrorFlag() {
      this.isInvalidCode = false
      this.isSystemError = false
    },
    async saveUser() {
      this.clearErrorFlag()
      this.sending = true
      try {
        let encryptedData = JSON.parse(this.$store.state.p);
        let iv = Uint8Array.from(encryptedData.iv);
        let p = Uint8Array.from(atob(encryptedData.v).split(''), char => char.charCodeAt(0));

        let decryptedArrayBuffer = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, this.$store.state.k, p)

        await Auth.confirmSignUp(this.$store.state.signupEmail, this.form.verificationCode)

        let decryptedP = (new TextDecoder()).decode(new Uint8Array(decryptedArrayBuffer));
        const user = await Auth.signIn(this.$store.state.signupEmail, decryptedP)

        /*
         * Issue cross-domain cookies for SHOP auto-login (centralised)
         */
        try {
          await issueShopAuthCookies({
            idToken: user.signInUserSession.idToken.jwtToken,
          })
        } catch (e) {
          console.error('[VerifySignup] issueShopAuthCookies failed', e)
          // Intentionally swallow the error so the user can proceed
        }

        const API_URL = apiUrl.change_opt_in_api_url(awsconfig.aws_user_pools_id)
        await this.$axios.post(API_URL,
          {
            token: user['signInUserSession']['idToken']['jwtToken'],
            isCreateUser: 1,
            isOptInEmail: this.$store.state.isOptInEmail === 'true' ? 1 : 0
          }
        ).then(function (response) {
        }).catch(function (response) {
          this.isSystemError = true
        });
        const referrer = this.$store.state.referrer
        this.$store.commit('clearAuthData')
        this.$store.commit('setUser', user)
        this.$emit('callSignin', user)
        this.$router.push({
          name: 'completeSignup',
          params: { ref: referrer }
        })
      } catch (error) {
        if (error.code === 'CodeMismatchException') {
          this.isInvalidCode = true
        } else {
          this.isSystemError = true
        }
      }
      this.sending = false
    },
    validateUser() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }
      this.saveUser()
    },
    async resendCode() {
      this.isResendCode = true;
      this.sending = true;

      setTimeout(async () => {
        if (this.isUserFound) {
          try {
            await Auth.resendSignUp(this.$store.state.signupEmail);
            this.codeSent = true;
            setTimeout(() => {
              this.codeSent = false;
            }, 5000);

          } catch (error) {
            this.isUserFound = false;
          }
        }
        this.isResendCode = false;
        this.sending = false;
      }, 1500);

      return false;
    },
  },
}
</script>
<!-- #endregion -->